import { INVENTORY_MODULE } from "@/api_framework/api_config";
import { useAPIUtils } from "@/app/hooks/useAPIUtils";
import { useConfiguration } from "@/app/hooks/useConfiguration";
import axios, { AxiosError, AxiosResponse } from "axios";
import { useCallback, useState } from "react";
import toast from "react-hot-toast";

export interface IDoctorInventoryCategory {
  id: string;
  label: string;
  background_color: string | null;
  text_color: string | null;
}

// Flattened row shape used by the UI table: one row per lot.
export interface IDoctorInventoryItem {
  unique_row_id: string;
  variant_id: string;
  variant_name: string | null;
  category: IDoctorInventoryCategory;
  lot_number: string;
  expiry_date: string | null;
  mrp: number;
  in_hand_quantity: number;
  blocked_inventory: number;
  available_quantity: number;
  transfer_request_number: string | null;
  transferred_at: number | null;
  // Distinct transfers this variant's in-hand stock came from; >1 means the same
  // product arrived across several TOs and appears as several rows.
  variant_transfer_count: number;
}

interface IDoctorInventoryLot {
  unique_row_id: string;
  lot_number: string;
  expiry: string | null;
  mrp: number;
  quantity: number;
  transfer_request_id: string | null;
  transfer_request_number: string | null;
  transferred_at: number | null;
}

interface IDoctorInventoryRawItem {
  variant_id: string;
  variant_name: string | null;
  category: IDoctorInventoryCategory;
  lots: IDoctorInventoryLot[];
}

export interface IDoctorInventoryResponseData {
  items: IDoctorInventoryItem[];
  total: number;
}

interface IDoctorInventoryRawData {
  clinic_id: string;
  clinic_name: string;
  categories: IDoctorInventoryCategory[];
  total_items: number;
  total_qty: number;
  items: IDoctorInventoryRawItem[];
}

interface IDoctorInventoryResponse {
  status: boolean;
  message: string;
  data: IDoctorInventoryRawData | null;
}

export interface IDoctorInventoryFilterBody {
  doctor_ids: string[];
  page: number;
  page_size: number;
}

export const useDoctorInventoryAPI = () => {
  const configuration = useConfiguration();
  const { captureError } = useAPIUtils();
  const [data, setData] = useState<IDoctorInventoryResponseData | null>(null);
  const [loading, setLoading] = useState(false);

  const execute = useCallback(
    async (filterBody: IDoctorInventoryFilterBody) => {
      setLoading(true);
      try {
        const accessToken = localStorage.getItem(configuration.localStorage.ACCESS_TOKEN.key);
        const response: IDoctorInventoryResponse = await axios
          .post(
            `${INVENTORY_MODULE.DOCTOR_INVENTORY_LIST.baseURL}?page_number=${filterBody.page}&page_size=${filterBody.page_size}`,
            { doctor_ids: filterBody.doctor_ids },
            {
              headers: {
                Authorization: `${accessToken}`,
                "Content-Type": "application/json",
              },
            },
          )
          .then((res: AxiosResponse<IDoctorInventoryResponse>) => {
            if (res.data.status === true) {
              return res.data;
            }
            return { status: false, message: "Failed to fetch", data: null };
          })
          .catch((e: AxiosError) => {
            captureError(e);
            return { status: false, message: "API Error", data: null };
          });

        if (response.status && response.data) {
          const items = (response.data.items ?? []).flatMap((item) => {
            const lots = (item.lots ?? []).filter((lot) => lot.quantity > 0);
            const variantTransferCount = new Set(lots.map((lot) => lot.transfer_request_id).filter(Boolean)).size;
            return lots.map((lot) => ({
              unique_row_id: lot.unique_row_id,
              variant_id: item.variant_id,
              variant_name: item.variant_name,
              category: item.category,
              lot_number: lot.lot_number,
              expiry_date: lot.expiry,
              mrp: lot.mrp,
              in_hand_quantity: lot.quantity,
              blocked_inventory: 0,
              available_quantity: lot.quantity,
              transfer_request_number: lot.transfer_request_number ?? null,
              transferred_at: lot.transferred_at ?? null,
              variant_transfer_count: variantTransferCount,
            }));
          });
          setData({ items, total: response.data.total_items ?? items.length });
        } else {
          setData(null);
        }
      } catch (e: any) {
        toast.error("Server Error: " + e.message);
        setData(null);
      } finally {
        setLoading(false);
      }
    },
    [configuration, captureError],
  );

  const reset = useCallback(() => {
    setData(null);
  }, []);

  return { data, loading, execute, reset };
};
